import pluralize from "pluralize";
import { useState } from "react";
import { Button } from "reactstrap";
import EditPrefillMenu from "../../CRM/EditPrefillMenu";
import useModule from "../../../hooks/useModule";
import AppDrawer from "../AppDrawer";
import { newToastEvent } from "utils/ToastUtil";
import AppSpinner from "../../../elements/AppSpinner";
import { useParams } from "react-router-dom";
import usePage from "../../../api/api-hooks/pages/usePage";
import StrUtils from "../../../utils/StrUtils";
import useBulkUpdateRecords from "../../../api/api-hooks/records/useBulkUpdateRecords";

const UpdateRecordsDrawer = ({
  open,
  onClose,
  records,
  moduleAPIName,
  size = "sm",
  placement = "right",
  backdrop = true,
  parentRecordID = null, // Providing this means we are updating subitems.
  multiSubitemAPIName = null, // Providing this means we are updating subitems.
  view,
}) => {
  const { pageID, workspaceID } = useParams();
  const { page } = usePage(pageID);
  const { module } = useModule(moduleAPIName, true);
  const [sections, setSections] = useState([]);
  const { bulkUpdate, startingUpdate } = useBulkUpdateRecords();

  const reset = () => {
    setSections([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubitemsUpdate = () => {
    const payload = {
      _UserActivity: {
        WorkspaceID: workspaceID,
        Activity: {
          id: StrUtils.uuidv4(),
          Type: "bulk-update-in-page",
          Page: {
            id: page.id,
            Name: page.Name,
            Logo: page.Logo,
          },
          ...(view && {
            View: {
              id: view.id,
              Name: view.Name,
              Type: view.Type,
            },
          }),
        },
      },
      Ids: [
        {
          APIName: multiSubitemAPIName,
          Ids: records,
          Value: sections.map((s) => ({ APIName: s.Field, Value: s.Value })),
        },
      ],
    };

    bulkUpdate({
      moduleAPIName: moduleAPIName,
      recordIds: [parentRecordID],
      payload: payload,
      onSuccess: () => {
        newToastEvent(
          "success",
          "Successfully started bulk update.",
          "bulk-update",
          { loading: true, autoClose: false }
        );
        handleClose();
      },
    });
  };

  const updateRecords = () => {
    if (parentRecordID) return handleSubitemsUpdate();

    const ids = records.map((r) => r?.id || r?._id);

    const payload = {
      _UserActivity: {
        WorkspaceID: workspaceID,
        Activity: {
          id: StrUtils.uuidv4(),
          Type: "bulk-update-in-page",
          Page: {
            id: page.id,
            Name: page.Name,
            Logo: page.Logo,
          },
          View: {
            id: view.id,
            Name: view.Name,
            Type: view.Type,
          },
        },
      },
      Ids: sections.map((s) => ({ APIName: s.Field, Value: s.Value })),
    };

    bulkUpdate({
      moduleAPIName: moduleAPIName,
      recordIds: ids,
      payload: payload,
      onSuccess: () => {
        newToastEvent(
          "success",
          "Successfully started bulk update.",
          "bulk-update",
          { loading: true, autoClose: false }
        );
        handleClose();
      },
    });
  };

  if (!module) return null;

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      size={size}
      placement={placement}
      backdrop={backdrop}
      headerTitle={
        "Update " +
        pluralize(
          module?.Name ? module.Name : "Record",
          records?.length ?? 1,
          true
        )
      }
      headerOptions={
        <Button
          size="sm"
          color="primary"
          onClick={updateRecords}
          disabled={startingUpdate || !sections?.length}
          style={{ width: 100 }}
        >
          {startingUpdate ? <AppSpinner size="sm" color="white" /> : "Update"}
        </Button>
      }
      bodyCSS="pt-2"
      body={
        <EditPrefillMenu
          module={module}
          prefillPairs={multiSubitemAPIName ? null : sections}
          onChange={setSections}
          editOnly={true}
          disabledFields={["Tags"]}
          multiSubitemAPIName={multiSubitemAPIName}
        />
      }
    />
  );
};

export default UpdateRecordsDrawer;
